// components/common/PackingModal/PackingModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import {
    FiX,
    FiPackage,
    FiBox,
    FiCheck,
    FiRefreshCw,
    FiAlertCircle,
    FiSave,
    FiSend,
    FiInfo
} from 'react-icons/fi';
import styles from './PackingModal.module.css';

const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

const PackingModal = ({ order, onClose, onConfirm, addToast, isLoading = false }) => {
    const [productsData, setProductsData] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [unitOfMeasure, setUnitOfMeasure] = useState('UN');
    const [unitOptions, setUnitOptions] = useState([]);
    const [loadingUnits, setLoadingUnits] = useState(true);
    const [grossWeight, setGrossWeight] = useState(0);
    const [netWeight, setNetWeight] = useState(0);
    const [totalBoxes, setTotalBoxes] = useState(0);
    const [packingNotes, setPackingNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch unit of measure options from Firestore
    const fetchUnitOptions = useCallback(async () => {
        setLoadingUnits(true);
        try {
            const unitsRef = collection(db, 'parameters/data/unidades-medida');
            const unitsSnapshot = await getDocs(unitsRef);
            const unitsList = [];

            unitsSnapshot.forEach((doc) => {
                const data = doc.data();
                unitsList.push({
                    id: doc.id,
                    value: data.name || doc.id,
                    label: data.name || doc.id,
                    conversionFactor: data.conversionFactor || 1
                });
            });

            // Sort alphabetically by label
            unitsList.sort((a, b) => a.label.localeCompare(b.label));

            setUnitOptions(unitsList);

            // Set default to first unit if available
            if (unitsList.length > 0) {
                setUnitOfMeasure(unitsList[0].value);
            }
        } catch (error) {
            console.error('Error fetching unit options:', error);
            // Fallback to default units if fetch fails
            setUnitOptions([
                { value: 'UN', label: 'Unidade', conversionFactor: 1 },
                { value: 'KG', label: 'Quilograma', conversionFactor: 1 },
                { value: 'TON', label: 'Tonelada', conversionFactor: 1000 },
                { value: 'M3', label: 'Metro Cúbico', conversionFactor: 1 },
                { value: 'CX', label: 'Caixa', conversionFactor: 1 },
                { value: 'PC', label: 'Peça', conversionFactor: 1 },
                { value: 'LT', label: 'Litro', conversionFactor: 1 },
                { value: 'MT', label: 'Metro', conversionFactor: 1 }
            ]);
            if (addToast) addToast('Erro ao carregar unidades de medida', 'Warning');
        } finally {
            setLoadingUnits(false);
        }
    }, [addToast]);

    // Fetch product details including NCM
    const fetchProductDetails = useCallback(async () => {
        if (!order?.items) return;

        setLoadingProducts(true);
        try {
            const enrichedItems = [];

            for (const item of order.items) {
                let productData = {};
                let ncm = '';
                let productGrossWeight = 0;
                let productNetWeight = 0;
                let unitsPerBox = 0;

                if (item.productId) {
                    try {
                        const productRef = doc(db, 'products', item.productId);
                        const productSnap = await getDoc(productRef);
                        if (productSnap.exists()) {
                            const data = productSnap.data();
                            productData = data;
                            ncm = data.ncm || data.NCM || '';
                            productGrossWeight = parseFloat(data.grossWeight) || parseFloat(data.bruto) || 0;
                            productNetWeight = parseFloat(data.netWeight) || parseFloat(data.liquido) || 0;
                            unitsPerBox = parseInt(data.unitsPerBox) || parseInt(data.technical?.unitsPerBox) || 0;
                        }
                    } catch (err) {
                        console.error(`Error fetching product ${item.productId}:`, err);
                    }
                }

                // Calculate boxes needed
                const quantity = parseFloat(item.quantity) || 0;
                let boxesNeeded = 0;
                if (unitsPerBox > 0 && quantity > 0) {
                    boxesNeeded = Math.ceil(quantity / unitsPerBox);
                } else if (quantity > 0) {
                    boxesNeeded = 1;
                }

                enrichedItems.push({
                    ...item,
                    ncm: ncm || item.ncm || '-',
                    grossWeight: productGrossWeight || item.grossWeight || 0,
                    netWeight: productNetWeight || item.netWeight || 0,
                    unitsPerBox: unitsPerBox || item.unitsPerBox || 0,
                    boxesNeeded: boxesNeeded,
                    productData: productData
                });
            }

            setProductsData(enrichedItems);

            // Calculate totals
            const totalGross = enrichedItems.reduce((sum, item) => sum + (item.grossWeight * item.quantity), 0);
            const totalNet = enrichedItems.reduce((sum, item) => sum + (item.netWeight * item.quantity), 0);
            const totalBoxes = enrichedItems.reduce((sum, item) => sum + (item.boxesNeeded || 0), 0);

            setGrossWeight(totalGross);
            setNetWeight(totalNet);
            setTotalBoxes(totalBoxes);

        } catch (error) {
            console.error('Error fetching product details:', error);
            if (addToast) addToast('Erro ao carregar detalhes dos produtos', 'Error');
        } finally {
            setLoadingProducts(false);
        }
    }, [order, addToast]);

    useEffect(() => {
        fetchUnitOptions();
        fetchProductDetails();
    }, [fetchUnitOptions, fetchProductDetails]);

    const handleConfirm = async () => {
        if (isSubmitting) return;

        // Validate
        if (productsData.length === 0) {
            if (addToast) addToast('Nenhum produto para embalar', 'Warning');
            return;
        }

        setIsSubmitting(true);
        try {
            // Find selected unit data
            const selectedUnit = unitOptions.find(u => u.value === unitOfMeasure);

            const packingData = {
                orderId: order.id,
                orderCode: order.code,
                items: productsData,
                unitOfMeasure: unitOfMeasure,
                unitOfMeasureLabel: selectedUnit?.label || unitOfMeasure,
                unitConversionFactor: selectedUnit?.conversionFactor || 1,
                grossWeight: grossWeight,
                netWeight: netWeight,
                totalBoxes: totalBoxes,
                packingNotes: packingNotes,
                status: 'packing_completed',
                packedAt: new Date(),
                packedBy: localStorage.getItem('userEmail') || 'Sistema'
            };

            await onConfirm(order.id, packingData);
            onClose();
        } catch (error) {
            console.error('Error confirming packing:', error);
            if (addToast) addToast(`Erro ao finalizar embalagem: ${error.message}`, 'Error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!order) return null;

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerLarge}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiPackage size={24} style={{ marginRight: '10px' }} /> Embalagem do Pedido</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {order.clientName}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        disabled={isSubmitting || isLoading}
                        aria-label="Fechar"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    {/* Summary Cards */}
                    <div className={styles.summaryCards}>
                        <div className={styles.summaryCard}>
                            <span className={styles.summaryLabel}>Total de Itens</span>
                            <span className={styles.summaryValue}>{productsData.length}</span>
                        </div>
                        <div className={styles.summaryCard}>
                            <span className={styles.summaryLabel}>Peso Bruto Total</span>
                            <span className={styles.summaryValue}>{grossWeight.toFixed(3)} kg</span>
                        </div>
                        <div className={styles.summaryCard}>
                            <span className={styles.summaryLabel}>Peso Líquido Total</span>
                            <span className={styles.summaryValue}>{netWeight.toFixed(3)} kg</span>
                        </div>
                        <div className={styles.summaryCard}>
                            <span className={styles.summaryLabel}>Caixas</span>
                            <span className={styles.summaryValue}>{totalBoxes}</span>
                        </div>
                    </div>

                    {/* Unit of Measure Selection */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiBox size={18} /></div>
                            <h3 className={styles.sectionTitle}>Unidade de Medida para Faturamento</h3>
                            {loadingUnits && (
                                <span className={styles.loadingBadge}>
                                    <FiRefreshCw size={14} className={styles.spinner} />
                                    Carregando...
                                </span>
                            )}
                        </div>
                        <div className={styles.unitSelector}>
                            <label className={styles.unitLabel}>
                                Unidade para Faturamento:
                                <select
                                    value={unitOfMeasure}
                                    onChange={(e) => setUnitOfMeasure(e.target.value)}
                                    className={styles.unitSelect}
                                    disabled={isSubmitting || isLoading || loadingUnits || unitOptions.length === 0}
                                >
                                    {loadingUnits ? (
                                        <option value="">Carregando unidades...</option>
                                    ) : unitOptions.length === 0 ? (
                                        <option value="">Nenhuma unidade disponível</option>
                                    ) : (
                                        unitOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </label>
                            {!loadingUnits && unitOptions.length > 0 && (
                                <span className={styles.unitCount}>
                                    {unitOptions.length} unidades disponíveis
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Products Table with NCM */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                            <h3 className={styles.sectionTitle}>Produtos</h3>
                            <span className={styles.sectionBadge}>
                                {productsData.length} itens
                            </span>
                        </div>
                        <div className={styles.tableWrapper}>
                            {loadingProducts ? (
                                <div className={styles.loadingState}>
                                    <FiRefreshCw size={24} className={styles.spinner} />
                                    <span>Carregando dados dos produtos...</span>
                                </div>
                            ) : (
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Produto</th>
                                            <th>NCM</th>
                                            <th className="text-right">Quantidade</th>
                                            <th className="text-right">Peso Bruto</th>
                                            <th className="text-right">Peso Líq.</th>
                                            <th className="text-right">Caixas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productsData.map((item, index) => (
                                            <tr key={item.id || index} className={item.isBonus ? styles.bonusRow : ''}>
                                                <td><strong>{item.code || '-'}</strong></td>
                                                <td>
                                                    {item.name || item.productName || '-'}
                                                    {item.isBonus && <span className={styles.bonusBadge}>BÔNUS</span>}
                                                    {item.isSample && <span className={styles.sampleBadge}>AMOSTRA</span>}
                                                </td>
                                                <td>
                                                    <span className={styles.ncmBadge}>
                                                        {item.ncm || '-'}
                                                    </span>
                                                </td>
                                                <td className="text-right">{parseFloat(item.quantity || 0).toFixed(3)}</td>
                                                <td className="text-right">
                                                    {(item.grossWeight * (item.quantity || 0)).toFixed(3)} kg
                                                </td>
                                                <td className="text-right">
                                                    {(item.netWeight * (item.quantity || 0)).toFixed(3)} kg
                                                </td>
                                                <td className="text-right">
                                                    <span className={styles.boxesBadge}>
                                                        {item.boxesNeeded || 0}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className={styles.totalRow}>
                                            <td colSpan="3"><strong>Totais</strong></td>
                                            <td className="text-right">
                                                <strong>{productsData.reduce((sum, i) => sum + parseFloat(i.quantity || 0), 0).toFixed(3)}</strong>
                                            </td>
                                            <td className="text-right">
                                                <strong>{grossWeight.toFixed(3)} kg</strong>
                                            </td>
                                            <td className="text-right">
                                                <strong>{netWeight.toFixed(3)} kg</strong>
                                            </td>
                                            <td className="text-right">
                                                <strong>{totalBoxes}</strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Observations */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiAlertCircle size={18} /></div>
                            <h3 className={styles.sectionTitle}>Observações de Embalagem</h3>
                        </div>
                        <div className={styles.notesArea}>
                            <textarea
                                value={packingNotes}
                                onChange={(e) => setPackingNotes(e.target.value)}
                                className={styles.textarea}
                                rows={3}
                                placeholder="Observações sobre a embalagem (opcional)..."
                                disabled={isSubmitting || isLoading}
                            />
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className={styles.infoBox} style={{ backgroundColor: 'var(--info-light)', borderLeftColor: 'var(--info-color)' }}>
                        <FiInfo size={20} />
                        <div>
                            <strong>Confirmação de Embalagem:</strong>
                            <p>
                                Ao confirmar, este pedido será enviado para o módulo de
                                <strong> Faturamento (Lançamento de NF's)</strong>
                                com os dados de embalagem registrados.
                            </p>
                        </div>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.secondaryButton}
                        disabled={isSubmitting || isLoading}
                    >
                        <FiX size={18} /> Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={styles.confirmButton}
                        disabled={isSubmitting || isLoading || loadingProducts || loadingUnits || unitOptions.length === 0}
                    >
                        {isSubmitting || isLoading ? (
                            <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Processando...</>
                        ) : (
                            <><FiSend size={18} /> Enviar para Faturamento</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PackingModal;